/**
 * Supplier Performance Component
 * Shows supplier scorecard and performance metrics
 */
import { Award, TrendingUp, TrendingDown, Star, Loader } from 'lucide-react';

const SupplierPerformance = ({ data, isFullScreen }) => {
  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <Award className="w-6 h-6 mr-2 text-blue-600" />
          Supplier Performance
        </h2>
        <div className="text-center py-8 text-gray-500">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p>Loading supplier data...</p>
        </div>
      </div>
    );
  }

  const suppliers = Array.isArray(data) ? data : data.suppliers || [];
  const topPerformers = suppliers.slice(0, 5);
  const avgOnTimeRate = data.avg_on_time_rate || 0;
  const avgQualityRate = data.avg_quality_rate || 0;

  const getGradeColor = (grade) => {
    const colors = {
      A: 'text-green-600',
      B: 'text-blue-600',
      C: 'text-yellow-600',
      D: 'text-orange-600',
      F: 'text-red-600',
    };
    return colors[grade] || 'text-gray-600';
  };

  const getGradeBg = (grade) => {
    const colors = {
      A: 'bg-green-100',
      B: 'bg-blue-100',
      C: 'bg-yellow-100',
      D: 'bg-orange-100',
      F: 'bg-red-100',
    };
    return colors[grade] || 'bg-gray-100';
  };

  const getRatingStars = (score) => {
    const stars = Math.round(score / 20); // Convert 0-100 to 0-5 stars
    return Array.from({ length: 5 }, (_, i) => i < stars);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className={`font-bold text-gray-800 flex items-center ${isFullScreen ? 'text-lg' : 'text-xl'}`}>
          <Award className="w-6 h-6 mr-2 text-blue-600" />
          Supplier Performance
        </h2>
        <span className="text-sm text-gray-600">Top 5 Suppliers</span>
      </div>

      {/* Average Metrics */}
      {(avgOnTimeRate > 0 || avgQualityRate > 0) && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {Math.round(avgOnTimeRate)}%
            </div>
            <div className="text-xs text-gray-600">Avg On-Time Rate</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {Math.round(avgQualityRate)}%
            </div>
            <div className="text-xs text-gray-600">Avg Quality Rate</div>
          </div>
        </div>
      )}

      {/* Supplier List */}
      <div className="space-y-3">
        {topPerformers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">No supplier data available</p>
          </div>
        ) : (
          topPerformers.map((supplier, index) => {
            const gradeColor = getGradeColor(supplier.grade);
            const gradeBg = getGradeBg(supplier.grade);
            const stars = getRatingStars(supplier.overall_score || 0);

            return (
              <div
                key={supplier.supplier_id || index}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-1">
                      <span className="font-bold text-gray-800">
                        {supplier.supplier_name || supplier.supplier_code || 'Unknown'}
                      </span>
                      <span className={`${gradeBg} ${gradeColor} px-3 py-1 rounded-full text-sm font-bold`}>
                        Grade {supplier.grade || 'N/A'}
                      </span>
                    </div>

                    {/* Star Rating */}
                    <div className="flex items-center space-x-1 mb-2">
                      {stars.map((filled, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${filled ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                        />
                      ))}
                      <span className="text-sm text-gray-600 ml-2">
                        {Math.round(supplier.overall_score || 0)}%
                      </span>
                    </div>

                    <div className="text-xs text-gray-600">
                      {supplier.supplier_code && (
                        <div>Code: {supplier.supplier_code}</div>
                      )}
                    </div>
                  </div>

                  {/* Rank Badge */}
                  <div className="flex-shrink-0 ml-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-100 text-yellow-700' :
                      index === 1 ? 'bg-gray-100 text-gray-700' :
                      index === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      #{index + 1}
                    </div>
                  </div>
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="bg-gray-50 rounded p-2">
                    <div className="text-sm font-bold text-gray-800">
                      {supplier.total_deliveries || 0}
                    </div>
                    <div className="text-xs text-gray-600">Deliveries</div>
                  </div>
                  <div className="bg-green-50 rounded p-2">
                    <div className="text-sm font-bold text-green-700">
                      {Math.round(supplier.on_time_rate || 0)}%
                    </div>
                    <div className="text-xs text-gray-600">On-Time</div>
                  </div>
                  <div className="bg-blue-50 rounded p-2">
                    <div className="text-sm font-bold text-blue-700">
                      {Math.round(supplier.quality_rate || 0)}%
                    </div>
                    <div className="text-xs text-gray-600">Quality</div>
                  </div>
                  <div className="bg-purple-50 rounded p-2">
                    <div className="text-sm font-bold text-purple-700">
                      {supplier.total_qc || 0}
                    </div>
                    <div className="text-xs text-gray-600">QC Tests</div>
                  </div>
                </div>

                {/* Performance Bars */}
                <div className="mt-3 space-y-2">
                  <div>
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span>On-Time Delivery</span>
                      <span>{Math.round(supplier.on_time_rate || 0)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-green-500 h-1.5 rounded-full transition-all duration-500"
                        style={{ width: `${supplier.on_time_rate || 0}%` }}
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span>Quality Score</span>
                      <span>{Math.round(supplier.quality_rate || 0)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                        style={{ width: `${supplier.quality_rate || 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* View All Link */}
      {suppliers.length > 5 && (
        <div className="mt-4 pt-4 border-t border-gray-200 text-center">
          <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
            View All Suppliers ({suppliers.length})
          </button>
        </div>
      )}
    </div>
  );
};

export default SupplierPerformance;
